import React, { useState } from 'react';
import { Check, Loader2, Send, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { CONSULTATION_REASONS, TIME_PREFERENCES } from '../../data/mock';

const AppointmentForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    reason: '',
    timePreference: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));

      if (onSubmit) {
        await onSubmit(formData);
      }

      setIsSubmitted(true);
      toast.success('¡Solicitud enviada!', {
        description: 'Te contactaremos pronto para confirmar.',
      });

      setTimeout(() => {
        setFormData({
          name: '',
          phone: '',
          email: '',
          reason: '',
          timePreference: '',
          message: '',
        });
        setIsSubmitted(false);
      }, 4000);
    } catch (error) {
      toast.error('Error al enviar', {
        description: 'Por favor intenta nuevamente.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
          <Check size={32} className="text-green-600" />
        </div>
        <h3 className="text-h4 mb-2">¡Solicitud recibida!</h3>
        <p className="text-body-sm mb-4">
          Gracias por contactarnos. Te llamaremos o enviaremos un WhatsApp en las próximas horas para confirmar tu
          cita.
        </p>
        <p className="text-xs text-[var(--text-muted)]">Si es urgente, llámanos directamente.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label htmlFor="name" className="label">
          Nombre completo
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="input"
          placeholder="¿Cómo te llamas?"
          autoComplete="name"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="phone" className="label">
            Teléfono / WhatsApp
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            className="input"
            placeholder="81 1234 5678"
            autoComplete="tel"
          />
        </div>
        <div>
          <label htmlFor="email" className="label">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="input"
            placeholder="tu@email.com"
            autoComplete="email"
          />
        </div>
      </div>

      <div>
        <label htmlFor="reason" className="label">
          ¿Qué te gustaría atender?
        </label>
        <select
          id="reason"
          name="reason"
          value={formData.reason}
          onChange={handleChange}
          required
          className="input select"
        >
          <option value="">Selecciona una opción</option>
          {CONSULTATION_REASONS.map((reason) => (
            <option key={reason} value={reason}>
              {reason}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="timePreference" className="label">
          Preferencia de horario
        </label>
        <select
          id="timePreference"
          name="timePreference"
          value={formData.timePreference}
          onChange={handleChange}
          required
          className="input select"
        >
          <option value="">Selecciona una opción</option>
          {TIME_PREFERENCES.map((time) => (
            <option key={time} value={time}>
              {time}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="message" className="label">
          Mensaje adicional <span className="font-normal text-[var(--text-muted)]">(opcional)</span>
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={3}
          className="input resize-none"
          placeholder="¿Algo más que debamos saber?"
        />
      </div>

      <button type="submit" disabled={isSubmitting} className="btn btn-primary btn-lg w-full">
        {isSubmitting ? (
          <>
            <Loader2 size={20} className="animate-spin" />
            Enviando...
          </>
        ) : (
          <>
            <Send size={20} />
            Solicitar cita
          </>
        )}
      </button>

      <div className="flex items-start gap-2 pt-2">
        <Shield size={14} className="text-[var(--text-muted)] mt-0.5 flex-shrink-0" />
        <p className="text-xs text-[var(--text-muted)]">
          Tus datos están seguros. Solo los usaremos para confirmar tu cita. Consulta nuestro{' '}
          <a href="#" className="underline hover:text-[var(--text-secondary)]">
            Aviso de Privacidad
          </a>
          .
        </p>
      </div>
    </form>
  );
};

export default AppointmentForm;
